// apps/web/src/config/env.ts
//
// Runtime config loader. Bundle is single; per-env values fetched from
// /env.json (same-origin on deployed hosts; remote on localhost).
//
// NEVER persist the resolved config blob in localStorage -- only the env
// id. A cached blob becomes a trapdoor on a buggy deploy.

import { z } from 'zod';

import { setApiBaseUrl } from '../services/api-client.ts';
import { authService } from '../services/auth.service.ts';

const STORAGE_KEY = 'breeze/active-env';
const SET_ENV_PARAM = 'setEnv';
const PROD_ENV = 'prod';
const SLUG_PLACEHOLDER = '{slug}';
const APP_SUBDOMAIN = 'app';

const DEV_DOMAIN_TEMPLATE = (import.meta.env.VITE_DEV_DOMAIN_TEMPLATE ?? '') as string;
const PROD_WEB_ORIGIN = (import.meta.env.VITE_CLOUDFRONT_BASE_URL_PROD ?? '') as string;
const DEFAULT_ENVIRONMENT = import.meta.env.VITE_ENVIRONMENT as string;
if (!DEFAULT_ENVIRONMENT) {
  throw new Error(
    'VITE_ENVIRONMENT is missing from the bundle. Rebuild with DEV_ENV or ' +
      'VITE_ENVIRONMENT set in the repo-root .env.',
  );
}

export const EnvConfigSchema = z.object({
  apiBaseUrl: z.string().url(),
  cognito: z.object({
    userPoolId: z.string(),
    clientId: z.string(),
    issuerUrl: z.string().url(),
    oauthDomain: z.string().url(),
    allowUsernamePasswordAuth: z.boolean(),
  }),
  features: z.record(z.boolean()).default({}),
});
export type EnvConfig = z.infer<typeof EnvConfigSchema>;

export class ConfigMissingError extends Error {
  readonly environment: string;
  constructor(environment: string, message: string) {
    super(message);
    this.name = 'ConfigMissingError';
    this.environment = environment;
  }
}

export function webOriginForEnv(env: string): string {
  if (env === PROD_ENV) {
    if (!PROD_WEB_ORIGIN) throw new Error('VITE_CLOUDFRONT_BASE_URL_PROD missing from bundle');
    return PROD_WEB_ORIGIN.replace(/\/+$/, '');
  }
  if (!DEV_DOMAIN_TEMPLATE.includes(SLUG_PLACEHOLDER)) {
    throw new Error(`VITE_DEV_DOMAIN_TEMPLATE must contain "${SLUG_PLACEHOLDER}"`);
  }
  return DEV_DOMAIN_TEMPLATE.replace(SLUG_PLACEHOLDER, env).replace(/\/+$/, '');
}

export function mapEnvJsonUrl(env: string): string {
  return `${webOriginForEnv(env)}/env.json`;
}

export function deriveEnvNameFromHostname(): string {
  if (typeof window === 'undefined') return DEFAULT_ENVIRONMENT;
  const host = window.location.hostname;
  if (host === 'localhost' || host === '127.0.0.1' || host === '[::1]') {
    return DEFAULT_ENVIRONMENT;
  }
  const labels = host.split('.');
  if (labels[0] === APP_SUBDOMAIN) return PROD_ENV;
  if (labels.length > 1 && labels[1] === APP_SUBDOMAIN) return labels[0] ?? '';
  return DEFAULT_ENVIRONMENT;
}

export function selectConfigId(): string {
  if (typeof window === 'undefined') return DEFAULT_ENVIRONMENT;

  const params = new URLSearchParams(window.location.search);
  const requested = params.get(SET_ENV_PARAM);
  if (requested) {
    const value = requested.trim();
    window.localStorage.setItem(STORAGE_KEY, value);
    params.delete(SET_ENV_PARAM);
    const remaining = params.toString();
    const newUrl =
      window.location.pathname +
      (remaining ? `?${remaining}` : '') +
      window.location.hash;
    window.history.replaceState(null, '', newUrl);
    return value;
  }

  const stored = window.localStorage.getItem(STORAGE_KEY);
  if (stored) return stored;

  return deriveEnvNameFromHostname();
}

export async function rehydrateConfig(): Promise<void> {
  const env = selectConfigId();
  const url = mapEnvJsonUrl(env);
  const fetchUrl = `${url}?t=${Date.now()}`;

  const response = await fetch(fetchUrl, {
    method: 'GET',
    headers: { Accept: 'application/json' },
    cache: 'no-store',
  });

  if (response.status === 404) {
    throw new ConfigMissingError(
      env,
      `Runtime configuration was not found for environment '${env}'. ` +
        'Deploy the env stack or check DEV_ENV in the repo-root .env.',
    );
  }
  if (!response.ok) {
    throw new Error(`Failed to fetch env config from ${url}: HTTP ${response.status}`);
  }

  const contentType = response.headers.get('content-type') ?? '';
  if (!contentType.toLowerCase().includes('application/json')) {
    throw new ConfigMissingError(
      env,
      `Configuration response for '${env}' was not JSON ` +
        `(Content-Type: ${contentType || '<unset>'}).`,
    );
  }

  const json: unknown = await response.json();
  const parsed = EnvConfigSchema.parse(json);
  applyConfig({
    ...parsed,
    apiBaseUrl: parsed.apiBaseUrl.replace(/\/+$/, ''),
  });
}

function applyConfig(config: EnvConfig): void {
  setApiBaseUrl(config.apiBaseUrl);
  authService.configure(config.cognito);
}
